// Import standards
import { useEffect, useState } from "react";
import axios from "../config/api";
import { useParams, useNavigate, Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";

// Bulma Components
import {
  Breadcrumb,
  Columns,
  Container,
  Heading,
  Image,
  Icon as BIcon,
  Media,
  Table,
  Box,
  Button,
  Level,
  Content,
  Tag,
} from "react-bulma-components";

// Components
import TimezoneClock from "../components/TimezoneClock";
import WeatherCard from "../components/WeatherCard";
// import HelloInLanguage from "../components/HelloInLanguage";
import NeighbourCountries from "../components/NeighbourCountry";
import Photos from "../components/Photos";

// MDI Icons
import Icon from "@mdi/react";
import {
  mdiAirplane,
  mdiEarth,
  mdiEarthBox,
  mdiHomeVariantOutline,
  mdiMapMarkerRadius,
  mdiInformationOutline,
} from "@mdi/js";

// Country Page
const Country = () => {
  // Utilised router-dom componenets
  let { name } = useParams();
  let navigate = useNavigate();

  const [country, setCountry] = useState(null);

  // get all countries
  useEffect(() => {
    axios
      .get(`/name/${name}?fullText=true`)
      .then((response) => {
        setCountry(response.data[0]);
      })
      .catch((error) => {
        navigate(`/country`);
      });
  }, [name, navigate]);

  // return nothing when no countries were found
  if (!country) return "";

  //  Local components to use with map
  const Name = (props) => {
    return <li>{props.name}</li>;
  };

  const Languages = (props) => {
    return <li>{props.language}</li>;
  };

  const Currencies = (props) => {
    return (
      <li>
        {props.currency} ({props.symbol})
      </li>
    );
  };

  // Native names mapping to return ordered list
  const nativeName = Object.entries(country.name.nativeName).map(
    ([key, value]) => {
      if (key !== "eng") return <Name key={key} name={value.official} />;
    }
  );

  // Languages mapping to return ordered list and array
  const languages = Object.values(country.languages).map((value, i) => (
    <Languages key={`${country.name.common}_language_${i}}`} language={value} />
  ));

  // Hello in different languages mapping to be return as components
  // const languagesHello = Object.values(country.languages).map((value, i) => (
  //   <HelloInLanguage
  //     key={`${country.name.common}_hello_${i}`}
  //     language={value}
  //     country={value}
  //   />
  // ));

  // Currencies map to return ordered list
  let currencies;

  if (country.currencies) {
    currencies = Object.values(country.currencies).map((value, i) => (
      <Currencies
        key={`${country.name.common}_currency_${i}}`}
        currency={value.name}
        symbol={value.symbol}
      />
    ));
    // if no currencies were found return no currency
  } else {
    currencies = <p>no currency</p>;
  }

  // RegExp to separate numbers using commas
  let formattedPop = country.population.toLocaleString("en-US");
  let formattedArea = country.area.toLocaleString("en-US");

  // Smooth scroll to element function
  const scrollWithOffset = (el) => {
    const yCoordinate = el.getBoundingClientRect().top + window.pageYOffset;
    const yOffset = -80;
    window.scrollTo({ top: yCoordinate + yOffset, behavior: "smooth" });
  };

  return (
    <>
      <Container className="is-fluid has-background-light">
        <Container pt="5">
          {/* Breadcrumbs and Close Button */}
          <Box>
            <Level>
              <Level.Side>
                <Level.Item>
                  <Breadcrumb>
                    <Breadcrumb.Item>
                      <Link to="/">
                        <BIcon>
                          <Icon path={mdiHomeVariantOutline}></Icon>
                        </BIcon>
                        Home
                      </Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                      {/* Smooth scroll function is used here */}
                      <HashLink
                        scroll={(el) => scrollWithOffset(el)}
                        to={`/region#${country.region}`}
                      >
                        <BIcon>
                          <Icon path={mdiEarth}></Icon>
                        </BIcon>
                        {country.region}
                      </HashLink>
                    </Breadcrumb.Item>
                    {country.subregion ? (
                      <Breadcrumb.Item>
                        <HashLink
                          scroll={(el) => scrollWithOffset(el)}
                          to={`/region#${country.subregion.replace(" ", "")}`}
                        >
                          <BIcon>
                            <Icon path={mdiEarthBox}></Icon>
                          </BIcon>
                          {country.subregion}
                        </HashLink>
                      </Breadcrumb.Item>
                    ) : null}
                    <Breadcrumb.Item active>
                      <Link to={`/country/${name}`}>
                        <BIcon>
                          <Icon path={mdiAirplane}></Icon>
                        </BIcon>
                        {country.name.common}
                      </Link>
                    </Breadcrumb.Item>
                  </Breadcrumb>
                </Level.Item>
              </Level.Side>

              <Level.Side>
                <Level.Item>
                  <Heading
                    pr={3}
                    textSize={6}
                    onClick={() => navigate(-1)}
                    renderAs="span"
                    clickable
                    subtitle
                  >
                    Close
                  </Heading>
                  <Button size="medium" onClick={() => navigate(-1)} remove />
                </Level.Item>
              </Level.Side>
            </Level>
          </Box>

          {/* Country Flag */}
          <Columns multiline={false}>
            <Columns.Column display="inline-block">
              <Image renderAs={Box} src={country.flags.png} size="16by9" />
            </Columns.Column>

            {/* Country Names Column */}
            <Columns.Column>
              <Box>
                <Media renderAs="article">
                  {country.coatOfArms.png ? (
                    <Media.Item renderAs="figure" align="left">
                      <Image size={32} src={country.coatOfArms.png} />
                    </Media.Item>
                  ) : (
                    ""
                  )}
                  <Media.Item>
                    {/* Display country name */}
                    <Heading
                      weight="bold"
                      textSize={2}
                      textTransform="uppercase"
                    >
                      {country.name.common} {/* Country subregion */}
                      <Tag
                        mb={2}
                        clickable
                        onClick={() => navigate(`/search/${country.subregion}`)}
                        color="link"
                      >
                        Region: {country.subregion}
                      </Tag>
                    </Heading>
                  </Media.Item>
                </Media>
                {/* Country's native names if existed */}
                <Content mt={3}>
                  {nativeName[0] ? (
                    <Heading
                      textSize={5}
                      ml="6"
                      pl="4"
                      textWeight="normal"
                      subtitle
                    >
                      Native name(s):
                      <ul className="is-size-6">{nativeName}</ul>
                    </Heading>
                  ) : (
                    ""
                  )}
                </Content>
              </Box>
              {/* Show its timezone and current weather forecast
                  Used Capital city as a condition to show/hide components
              */}
              <Columns>
                {country.capital ? (
                  <>
                    <TimezoneClock
                      local={country.timezones[0]}
                      latlon={country.latlng}
                    />
                    <WeatherCard latlon={country.latlng} />
                  </>
                ) : (
                  <WeatherCard size="12" latlon={country.latlng} />
                )}
              </Columns>
              {/* Hello Components (UNUSED) */}
              {/* {languagesHello} */}
            </Columns.Column>
          </Columns>

          {/* Table About The Country */}
          <Columns>
            <Columns.Column size={9}>
              <Columns>
                <Columns.Column display="flex" size={4}>
                  <Box>
                    <Table size="fullwidth" hoverable>
                      <thead>
                        <tr className="is-align-items-center has-text-center">
                          <th
                            className="has-text-center is-uppercase is-size-5"
                            colSpan="2"
                          >
                            <Icon
                              size={0.8}
                              className="has-text-link"
                              path={mdiInformationOutline}
                            />{" "}
                            About {country.name.common}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Region */}
                        <tr>
                          <th className="has-background-link is-selected is-capitalized">
                            Region
                          </th>
                          <td>{country.region}</td>
                        </tr>
                        {/* Area in Sq Km */}
                        <tr>
                          <th className="has-background-link is-selected is-capitalized">
                            Area
                          </th>
                          <td>
                            {formattedArea} km<sup>2</sup>
                          </td>
                        </tr>
                        {/* Capital city */}
                        <tr>
                          <th className="has-background-link is-selected is-capitalized">
                            Capital City
                          </th>
                          <td>{country.capital}</td>
                        </tr>
                        {/* Population */}
                        <tr>
                          <th className="has-background-link is-selected is-capitalized">
                            population
                          </th>
                          <td>{formattedPop} citizens</td>
                        </tr>
                        {/* Languages */}
                        <tr>
                          <th className="has-background-link is-selected">
                            Language(s)
                          </th>
                          <td>
                            {languages.length > 1 ? (
                              <ol className="ml-4">{languages}</ol>
                            ) : (
                              <ul>{languages}</ul>
                            )}
                          </td>
                        </tr>
                        {/* Currencies if existed */}
                        <tr>
                          <th className="has-background-link is-selected is-capitalized">
                            currencies
                          </th>
                          <td>
                            {currencies.length > 1 ? (
                              <ol className="ml-4">{currencies}</ol>
                            ) : (
                              <ul>{currencies}</ul>
                            )}
                          </td>
                        </tr>
                        {/* Its latitude and longtitude */}
                        <tr>
                          <th className="has-background-link is-selected is-capitalized">
                            Latitude, Lontitude
                          </th>
                          <td>
                            {country.latlng[0]}, {country.latlng[1]}
                          </td>
                          {/* Demonyms */}
                        </tr>
                        {country.demonyms ? (
                          <tr>
                            <th className="has-background-link is-selected is-capitalized">
                              Demonyms
                            </th>
                            <td>
                              <li>{country.demonyms.eng.m} (m.)</li>
                              <li>{country.demonyms.eng.f} (f.)</li>
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </Table>
                  </Box>
                </Columns.Column>

                {/* Location on map using Google Iframe */}
                <Columns.Column display="flex" size={7}>
                  <Box>
                    <Heading textColor="text" size={4} alignItems="center">
                      <Icon
                        className="has-text-link is-align-self-center pt-3"
                        size={1.5}
                        path={mdiMapMarkerRadius}
                      />
                      LOCATION ON MAP
                    </Heading>
                    <div className="mapouter">
                      <div className="gmap_canvas">
                        <iframe
                          title="googleMap"
                          width="610"
                          height="513"
                          id="gmap_canvas"
                          src={`https://maps.google.com/maps?q=${
                            country.capital ? country.capital : null
                          }%20%${
                            country.name.common
                          }&t=&z=5&ie=UTF8&iwloc=&output=embed`}
                        ></iframe>
                      </div>
                    </div>
                  </Box>
                </Columns.Column>

                {/* Photos API */}
                <Columns.Column size={12} pr={5}>
                  <Photos
                    location={country.capital[0]}
                    name={country.name.common}
                  />
                </Columns.Column>
              </Columns>
            </Columns.Column>

            {/* Show Neighbour Countries If Existed */}
            {country.borders ? (
              <Columns.Column
                fullhd={{ size: 3 }}
                mobile={{ flexDirection: "column", size: 12 }}
                desktop={{ size: 3 }}
                renderAs={Box}
                paddingless
                mt={3}
                mb={5}
              >
                <NeighbourCountries
                  country={country.name.common}
                  cca3={country.borders}
                />
              </Columns.Column>
            ) : (
              ""
            )}
          </Columns>
        </Container>
      </Container>
    </>
  );
};

export default Country;
